/**
 * iter845i — Cashier quick-toggle for online ordering.
 *
 * Reads `online_ordering_enabled` from the branch config and lets the
 * cashier flip it OFF (pause) with one tap during a rush, then turn it
 * back ON. Renders as a small pill in the POS header. Hidden when the
 * branch has never enabled online ordering — no clutter for shops that
 * don't use the feature.
 *
 * Uses PUT /api/branches/{id}/online-config which the same admin page
 * calls, so the change is instantly reflected in /order/{slug} — a
 * customer refreshing the public menu will see "closed for online
 * ordering" within seconds.
 */
import { useEffect, useState, useCallback } from 'react';
import { Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export const PauseOnlineOrdersChip = ({ branchId }) => {
  const [status, setStatus] = useState(null); // null=loading; true=on; false=paused; 'na'=never enabled
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    // Note: branchId is not strictly required — the backend uses the
    // cashier's own branch context from the JWT. We keep the prop for
    // future manual branch-switching support.
    try {
      const { data } = await api.get('/cashier/online-status');
      if (!data?.has_config || !data.online_slug) { setStatus('na'); return; }
      setStatus(!!data.online_ordering_enabled);
    } catch { setStatus('na'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (status === null || status === 'na') return null;

  const toggle = async () => {
    setSaving(true);
    try {
      const next = !status;
      await api.post('/cashier/online-status/toggle', { enabled: next });
      setStatus(next);
      toast.success(next ? 'Online ordering resumed' : 'Online ordering paused — customers see "closed"');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const on = status === true;
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={saving}
      title={on ? 'Online ordering is ON — tap to pause' : 'Online ordering is PAUSED — tap to resume'}
      data-testid="pause-online-orders-chip"
      className={`inline-flex items-center gap-1.5 h-10 px-2.5 rounded-full text-[10px] font-bold border transition-colors ${
        on
          ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-300'
          : 'bg-rose-50 border-rose-300 text-rose-700 hover:bg-rose-100 dark:bg-rose-950/40 dark:border-rose-800 dark:text-rose-300'
      }`}
    >
      {on ? <Wifi size={11} /> : <WifiOff size={11} />}
      <span>{on ? 'Online' : 'Paused'}</span>
    </button>
  );
};

export default PauseOnlineOrdersChip;
